<template>
    <div class="animated fadeIn">
        <div class="kgr-postfix kgr-postfix--gutters kgr-postfix--top kgr-row">
            <kgr-exam-result-filter
                :club-id="clubId"
                :loading="loading"
                :club-exam-id="clubExamId"
                storage-key="clubExamResultFilter"
                show-export-button
                @onFilter="load($event)"
                @onExport="exportExcel($event)"
            />
        </div>
        <table class="kgr-table kgr-table--striped kgr-table--block table-groups kgr-row">
            <thead>
                <tr class="kgr-table__row">
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('club.exam.result.examinee') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('club.exam.title') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Prüfungsdatum') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('club.exam.type') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('club.exam.sport') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('club.exam.result.status') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="loading || reloading">
                    <td
                        colspan="6"
                        style="text-align: center;"
                    >
                        <kgr-loader />
                    </td>
                </tr>
                <tr v-else-if="clubExamResults.length === 0">
                    <td
                        colspan="6"
                        style="text-align: center;"
                    >
                        {{ $t('dataTable.noData') }}
                    </td>
                </tr>
                <template v-else>
                    <tr
                        v-for="examResult in clubExamResults"
                        :key="examResult.id"
                        class="kgr-table__row"
                    >
                        <td class="kgr-table__cell">
                            <a
                                v-if="examResult.member"
                                class="kgr-link"
                                :href="memberExamResultsUrl(examResult.member.id)"
                            >
                                {{ examResult.member.name }}
                            </a>
                            <a
                                v-if="examResult.dog"
                                class="kgr-link"
                                :href="dogExamResultsUrl(examResult.dog.id)"
                            >
                                {{ examResult.dog.name }}
                            </a>
                        </td>
                        <td class="kgr-table__cell">
                            {{ examResult.clubExam.title }}
                        </td>
                        <td class="kgr-table__cell">
                            {{ examResult.examDate }}
                        </td>
                        <td class="kgr-table__cell">
                            {{ examResult.clubExam.clubExamType ? examResult.clubExam.clubExamType.title : '' }}
                        </td>
                        <td class="kgr-table__cell">
                            {{ examResult.clubExam.sport ? examResult.clubExam.sport.name : '' }}
                        </td>
                        <td class="kgr-table__cell">
                            <kgr-exam-result-status-icon
                                :exam-result="examResult"
                            />
                        </td>
                        <td
                            class="kgr-table__cell"
                            style="vertical-align: top;"
                        >
                            <a
                                :title="$t('Prüfungsergebnis bearbeiten')"
                                :href="editExamResultUrl(examResult)"
                            >
                                <kgr-icon-edit size="15" />
                            </a>
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>
        <kgr-paginator
            v-show="!loading"
            v-model="paginator"
            :length="clubExamResultCount"
            @post-input="reload($event)"
        />
    </div>
</template>

<script>
import ClubExamResultRepository from '../../../js/repository/club/ClubExamResultRepository';
import Error from '../../../js/model/Error';
import ExamResultFilter from '../../../components/exams/ExamResultFilter.vue';
import ExamResultStatusIcon from '../../../components/exams/ExamResultStatusIcon.vue';
import IconEdit from '../../../components/common/icon/IconEdit.vue';
import KgrPaginator from '../../../components/common/Paginator';
import Loading from '../../../components/common/Loading.vue';

export default {
    components: {
        'kgr-exam-result-filter': ExamResultFilter,
        'kgr-exam-result-status-icon': ExamResultStatusIcon,
        'kgr-icon-edit': IconEdit,
        'kgr-loader': Loading,
        'kgr-paginator': KgrPaginator,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        currentPath: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            error: new Error(),
            loading: false,
            reloading: false,
            clubExamResults: [],
            clubExamId: null,
            filter: null,
            clubExamResultCount: 0,
            paginator: {
                limit: 10,
                offset: 0,
            },
        };
    },
    created() {
        this.parseQuery();
    },
    methods: {
        parseQuery() {
            const searchParams = new URLSearchParams(window.location.search);
            if (searchParams.has('clubExamId')) {
                this.clubExamId = searchParams.get('clubExamId');
                this.loading = true;
            }
        },
        load(filter = null) {
            if (filter) {
                this.filter = filter;
                this.loading = true;
                this.paginator.offset = 0;
                this.loadCount();
            } else {
                this.reloading = true;
            }
            ClubExamResultRepository.findByClubId(this.clubId, this.filter, this.paginator.limit, this.paginator.offset)
                .then((response) => this.clubExamResults = response)
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => {
                    this.reloading = false;
                    this.loading = false;
                });
        },
        reload(paginator) {
            this.paginator = paginator;
            this.load();
        },
        loadCount() {
            ClubExamResultRepository.countByClubId(this.clubId, this.filter)
                .then((response) => {
                    this.clubExamResultCount = response;
                });
        },
        exportExcel(filter) {
            this.loading = true;
            ClubExamResultRepository.getExcelByClubIdAndQuery(this.clubId, filter)
                .then(() => {})
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => this.loading = false);
        },
        memberExamResultsUrl(memberId) {
            return `/backend/clubs/members/${memberId}/exams`;
        },
        dogExamResultsUrl(dogId) {
            return `/backend/clubs/dogs/${dogId}/exams`;
        },
        editExamResultUrl(examResult) {
            return `/backend/clubs/${this.clubId}/exams/results/edit/${examResult.id}`;
        },
        iconClass(examResult) {
            if (examResult.isFailure()) {
                return 'close';
            }
            if (examResult.isPending()) {
                return 'clock';
            }
            if (examResult.isSuccess()) {
                return 'check';
            }
        },
    },
};
</script>

<style scoped lang="scss"></style>
